import React, { useState, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import useViewerIdentity from '../../hooks/useViewerIdentity';
import { submitReport } from '../../services/reportsApi';
import Button from './ui/Button';
import './ReportIssueButton.css';

const CATEGORIES = [
  { value: 'BUG', label: 'Bug / Something broken' },
  { value: 'CONTENT', label: 'Content issue (typos, wrong data)' },
  { value: 'ABUSE', label: 'Abuse / Harassment' },
  { value: 'PERFORMANCE', label: 'Performance problem' },
  { value: 'FEATURE_REQUEST', label: 'Feature request' },
  { value: 'OTHER', label: 'Other' },
];

const ReportIssueButton = ({ prefillCategory, prefillErrorMessage, prefillDescription }) => {
  const { user } = useViewerIdentity();
  const location = useLocation();
  const [isOpen, setIsOpen] = useState(false);
  const [category, setCategory] = useState('');
  const [description, setDescription] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState(null);

  const handleOpen = useCallback(() => {
    setCategory(prefillCategory || '');
    setDescription(prefillDescription || '');
    setSuccess(false);
    setError(null);
    setIsOpen(true);
  }, [prefillCategory, prefillDescription]);

  const handleClose = useCallback(() => {
    setIsOpen(false);
    setCategory('');
    setDescription('');
    setSuccess(false);
    setError(null);
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!category) {
      setError('Please select a category.');
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      await submitReport({
        category,
        description: description.trim() || null,
        pageUrl: typeof window !== 'undefined' ? window.location.href : null,
        pageRoute: location.pathname,
        userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : null,
        errorMessage: prefillErrorMessage || null,
      });
      setSuccess(true);
    } catch (err) {
      if (err?.response?.status === 401) {
        setError('Please log in to submit a report.');
      } else {
        setError(err?.response?.data?.message || 'Failed to submit report. Please try again.');
      }
    } finally {
      setSubmitting(false);
    }
  };

  const isChapterReader = /^\/novels\/\d+\/chapters\/\d+/.test(location.pathname);

  return (
    <>
      <button
        className={`report-issue-fab${isChapterReader ? ' report-issue-fab--reader' : ''}`}
        onClick={handleOpen}
        title="Report an issue"
        aria-label="Report an issue"
      >
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M4 15s1-1 4-1 5 2 8 2 4-1 4-1V3s-1 1-4 1-5-2-8-2-4 1-4 1z" />
          <line x1="4" y1="22" x2="4" y2="15" />
        </svg>
        <span className="report-issue-fab__label">Report Issue</span>
      </button>

      {isOpen && (
        <div className="report-issue-overlay" onClick={handleClose}>
          <div className="report-issue-modal" onClick={(e) => e.stopPropagation()}>
            {success ? (
              <div className="report-issue-modal__success">
                <div className="report-issue-modal__success-icon">
                  <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
                    <polyline points="22 4 12 14.01 9 11.01" />
                  </svg>
                </div>
                <h2>Report Submitted</h2>
                <p>Thank you! Your report has been submitted and will be reviewed by our team.</p>
                <Button variant="primary" onClick={handleClose}>Close</Button>
              </div>
            ) : (
              <>
                <div className="report-issue-modal__header">
                  <h2>Report an Issue</h2>
                  <button className="report-issue-modal__close" onClick={handleClose} aria-label="Close">
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <line x1="18" y1="6" x2="6" y2="18" />
                      <line x1="6" y1="6" x2="18" y2="18" />
                    </svg>
                  </button>
                </div>

                <form className="report-issue-modal__form" onSubmit={handleSubmit}>
                  <div className="report-issue-modal__field">
                    <label htmlFor="report-category" className="report-issue-modal__label">
                      Category <span className="report-issue-modal__required">*</span>
                    </label>
                    <select
                      id="report-category"
                      className="report-issue-modal__select"
                      value={category}
                      onChange={(e) => setCategory(e.target.value)}
                      required
                      disabled={submitting}
                    >
                      <option value="">Select a category...</option>
                      {CATEGORIES.map((c) => (
                        <option key={c.value} value={c.value}>{c.label}</option>
                      ))}
                    </select>
                  </div>

                  <div className="report-issue-modal__field">
                    <label htmlFor="report-description" className="report-issue-modal__label">
                      Description <span className="report-issue-modal__optional">(optional)</span>
                    </label>
                    <textarea
                      id="report-description"
                      className="report-issue-modal__textarea"
                      placeholder="Describe what happened..."
                      value={description}
                      onChange={(e) => setDescription(e.target.value)}
                      maxLength={2000}
                      rows={4}
                      disabled={submitting}
                    />
                    <span className="report-issue-modal__char-count">{description.length}/2000</span>
                  </div>

                  <div className="report-issue-modal__debug-info">
                    <p className="report-issue-modal__debug-title">Auto-captured info:</p>
                    <div className="report-issue-modal__debug-row">
                      <span>URL:</span>
                      <code>{typeof window !== 'undefined' ? window.location.href : ''}</code>
                    </div>
                    <div className="report-issue-modal__debug-row">
                      <span>Route:</span>
                      <code>{location.pathname}</code>
                    </div>
                  </div>

                  {error && (
                    <div className="report-issue-modal__error" role="alert">
                      {error}
                    </div>
                  )}

                  <div className="report-issue-modal__actions">
                    <Button type="button" variant="ghost" onClick={handleClose} disabled={submitting}>
                      Cancel
                    </Button>
                    <Button type="submit" variant="primary" isLoading={submitting} disabled={submitting}>
                      Submit Report
                    </Button>
                  </div>
                </form>
              </>
            )}
          </div>
        </div>
      )}
    </>
  );
};

export default ReportIssueButton;
